/**
 * Registers the self-hosted Pretendard web fonts at startup, via the FontFace
 * API rather than an @font-face in index.html. Two reasons this beats declarative
 * CSS here:
 *
 *  1. The font URL must carry SHARED_ORIGIN (shared assets are served cross-origin
 *     from the website host on the PWA host — see frontend/CLAUDE.md). Taking it
 *     from lib/assets means a missing/empty env resolves to a real origin instead
 *     of leaving a literal `%VITE_SHARED_ORIGIN%` in an index.html <style>.
 *  2. A scripted font load is governed by script-src, not style-src, so it does
 *     not depend on `style-src 'unsafe-inline'` — which the CSP hardening plan
 *     aims to drop.
 *
 * The faces are only *registered*, never eagerly loaded: the browser fetches a
 * face when a rendered glyph actually needs it. So the ~5 MB Pretendard JP file
 * is pulled only on Japanese pages (`html[lang='ja']`, set in tokens.css) — and
 * `<html lang>` is set from the stored locale before first render (see main.tsx)
 * so a cold Japanese session never requests the base font first.
 */

import { SHARED_ORIGIN } from '@/lib/assets';

// [CSS family alias, file in shared/fonts/]. Weight is the variable axis range.
const FACES: ReadonlyArray<readonly [string, string]> = [
  ['Pretendard', 'PretendardVariable.woff2'],
  ['Pretendard JP', 'PretendardJPVariable.woff2'],
];

/** Register the Pretendard faces so the CSS `--font-sans` stacks can resolve to
 *  them. Safe to call once at startup; a no-op where FontFace is unavailable. */
export function registerFonts(): void {
  // Non-browser / unsupported contexts (SSR, tests): the system-font fallback
  // stack in --font-sans keeps text readable, so skip silently.
  if (typeof document === 'undefined' || !('fonts' in document)) return;

  for (const [family, file] of FACES) {
    try {
      const face = new FontFace(family, `url("${SHARED_ORIGIN}/fonts/${file}") format("woff2")`, {
        style: 'normal',
        weight: '45 930', // Pretendard variable wght axis
        display: 'swap',
      });
      document.fonts.add(face);
      // Intentionally NOT calling face.load(): let the browser fetch each face
      // on demand, so Pretendard JP stays off non-Japanese sessions.
    } catch {
      // Malformed descriptor or unsupported API — fall back to the system stack.
    }
  }
}
